import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { DollarSign, Tag, RefreshCw, ChevronDown, ChevronRight, Zap, Calendar } from 'lucide-react';
import { SkeletonRows, EmptyGoodNews, ErrorBanner } from './shell/carbonStates';

// ============================================================================
// CHA-277 — Alerts Carbon pass (visual only; design: ChainTo Alerts & Data)
// ============================================================================
// Same data, same numbers: the getMarginAlerts callable and every computed
// total are unchanged from the pre-reskin page. Only the chrome is Carbon —
// KPI tile strip, anomaly rows with 3px severity rails + count column, mono
// grid tables, canonical states from shell/carbonStates.
//
// Concept-vs-reality note: the concept's "RESOLVED — 7D" KPI has no backing
// data (the callable returns open alerts only) — it is intentionally NOT
// built. The 4th tile maps the real at-risk gross value instead.

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtCur = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct = (n) => Number(n || 0).toFixed(1) + '%';

// Severity meta — Carbon severity rails/tiles (design tokens).
const SEV = {
  high: { rail: '#DA1E28', tileBg: '#FFF1F1', ink: '#DA1E28' },
  med: { rail: '#FF832B', tileBg: '#FFF8F1', ink: '#BA4E00' },
};

// Group key — single source of truth for both groupRows() and the
// StyleGroupTable expand-toggle key. Keeping them in sync is what fixes
// Bug 1 (CHA-38) and prevents its re-occurrence.
const getGroupKey = (r) => `${r.style}||${r.customer}||${r.division}||${r.company}`;

// Group rows by style + customer + division + company
function groupRows(rows) {
  const map = {};
  rows.forEach(r => {
    const key = getGroupKey(r);
    if (!map[key]) {
      map[key] = {
        style: r.style, styleName: r.styleName, customer: r.customer,
        division: r.division, company: r.company, orders: [],
        totalQty: 0, totalGrossPrice: 0, totalCost: 0, totalGP: 0,
      };
    }
    map[key].orders.push(r);
    map[key].totalQty += r.orderQty;
    map[key].totalGrossPrice += r.grossPrice;
    map[key].totalCost += r.cost;
    map[key].totalGP += r.grossProfit;
  });
  return Object.values(map).map(g => ({
    ...g,
    gpPercent: g.totalGrossPrice ? (g.totalGP / g.totalGrossPrice) * 100 : 0,
  })).sort((a, b) => b.totalGrossPrice - a.totalGrossPrice);
}

// Get Monday of the week for a given YYYY-MM-DD date string.
// Parses as a local-TZ date so subsequent local-TZ math (getDay/setDate)
// stays consistent — avoids the UTC-midnight-rolls-back-a-day bug in US
// timezones.
function getMonday(d) {
  const [y, m, day] = d.split('-').map(Number);
  const dt = new Date(y, m - 1, day);
  const dow = dt.getDay();
  const diff = dow === 0 ? 6 : dow - 1;
  dt.setDate(dt.getDate() - diff);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Format date range label for a week
function weekLabel(mondayStr) {
  const mon = new Date(mondayStr + 'T00:00:00');
  const sun = new Date(mon);
  sun.setDate(sun.getDate() + 6);
  const fmtD = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
  return `${fmtD(mon)} - ${fmtD(sun)}`;
}

// Split rows into Pick section + Open grouped by StartDate week
function splitBySection(rows) {
  const pickRows = rows.filter(r => r.status === 'P');
  const openRows = rows.filter(r => r.status === 'O');

  // Group open by StartDate week
  const weekMap = {};
  openRows.forEach(r => {
    const sd = r.startDate ? r.startDate.slice(0, 10) : '';
    const monday = sd ? getMonday(sd) : '9999-99-99';
    if (!weekMap[monday]) weekMap[monday] = [];
    weekMap[monday].push(r);
  });

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const thisMonday = getMonday(today);

  const openWeeks = Object.entries(weekMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monday, wRows]) => {
      let label;
      if (monday === '9999-99-99') label = 'No Start Date';
      else if (monday < thisMonday) label = `Past Due (${weekLabel(monday)})`;
      else if (monday === thisMonday) label = `This Week (${weekLabel(monday)})`;
      else label = `Week of ${weekLabel(monday)}`;
      return { monday, label, rows: wRows, isPastDue: monday < thisMonday && monday !== '9999-99-99' };
    });

  return { pickRows, openWeeks };
}

// Mono uppercase table header (Orders reference anatomy).
const TH_CLS = 'px-2.5 py-2 font-plex-mono text-[10px] font-medium tracking-[0.12em] uppercase text-carbon-gray-50 whitespace-nowrap select-none';

function StyleGroupTable({ rows }) {
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [sortKey, setSortKey] = useState('totalGrossPrice');
  const [sortAsc, setSortAsc] = useState(false);

  const groups = groupRows(rows);

  const sorted = [...groups].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'number' && typeof bv === 'number') return sortAsc ? av - bv : bv - av;
    return sortAsc ? String(av || '').localeCompare(String(bv || '')) : String(bv || '').localeCompare(String(av || ''));
  });

  const toggleExpand = (key) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const COLS = [
    { key: '_expand', label: '', align: 'center', w: 'w-8' },
    { key: 'style', label: 'Style', align: 'left' },
    { key: 'styleName', label: 'Style Name', align: 'left' },
    { key: 'customer', label: 'Customer', align: 'left' },
    { key: 'division', label: 'Division', align: 'left' },
    { key: 'orders', label: 'Orders', align: 'right' },
    { key: 'totalQty', label: 'Total Qty', align: 'right' },
    { key: 'totalGrossPrice', label: 'Total Price', align: 'right' },
    { key: 'totalCost', label: 'Total Cost', align: 'right' },
    { key: 'totalGP', label: 'Total GP', align: 'right' },
    { key: 'gpPercent', label: 'GP%', align: 'right' },
  ];

  if (sorted.length === 0) {
    return <div className="px-[18px] py-5 text-center text-carbon-gray-50 text-xs">No alerts</div>;
  }

  return (
    <table className="w-full text-xs font-plex text-carbon-ink">
      <thead>
        <tr className="bg-carbon-gray-10">
          {COLS.map(col => (
            <th key={col.key}
              onClick={col.key !== '_expand' ? () => handleSort(col.key) : undefined}
              className={`${TH_CLS} ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'} ${col.key !== '_expand' ? 'cursor-pointer hover:text-carbon-ink' : ''} ${col.w || ''}`}>
              {col.label}
              {sortKey === col.key && <span className="ml-1">{sortAsc ? '▲' : '▼'}</span>}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((g) => {
          const key = getGroupKey(g);
          const isExpanded = expandedKeys.has(key);
          return (
            <React.Fragment key={key}>
              <tr className="bg-carbon-white border-t border-carbon-gray-10 hover:bg-carbon-gray-10 cursor-pointer"
                onClick={() => toggleExpand(key)}>
                <td className="px-2.5 py-2 text-center">
                  {isExpanded ? <ChevronDown size={14} className="text-carbon-gray-40 inline" /> : <ChevronRight size={14} className="text-carbon-gray-40 inline" />}
                </td>
                <td className="px-2.5 py-2 font-plex-mono font-medium">{g.style}</td>
                <td className="px-2.5 py-2 text-carbon-gray-70">{g.styleName || '-'}</td>
                <td className="px-2.5 py-2 text-carbon-gray-70">{g.customer}</td>
                <td className="px-2.5 py-2 text-carbon-gray-50">{g.division}</td>
                <td className="px-2.5 py-2 text-right font-plex-mono text-carbon-gray-70">{g.orders.length}</td>
                <td className="px-2.5 py-2 text-right font-plex-mono">{fmt(g.totalQty)}</td>
                <td className="px-2.5 py-2 text-right font-plex-mono font-medium">{fmtCur(g.totalGrossPrice)}</td>
                <td className="px-2.5 py-2 text-right font-plex-mono text-carbon-gray-70">{fmtCur(g.totalCost)}</td>
                <td className="px-2.5 py-2 text-right font-plex-mono text-carbon-gray-70">{fmtCur(g.totalGP)}</td>
                <td className="px-2.5 py-2 text-right font-plex-mono font-semibold">{fmtPct(g.gpPercent)}</td>
              </tr>
              {isExpanded && g.orders.map((o, j) => (
                <tr key={`${key}-${j}`} className="border-t border-carbon-gray-10 bg-[#FAFAFA]">
                  <td className="px-2.5 py-1.5"></td>
                  <td className="px-2.5 py-1.5 pl-6">
                    <span className={`font-plex-mono text-[9.5px] tracking-[0.08em] px-2 py-[2px] border ${o.status === 'P' ? 'text-carbon-blue-70 border-carbon-blue-50' : 'text-carbon-gray-70 border-carbon-gray-30'}`}>
                      {o.status === 'P' ? 'PICK' : 'OPEN'}
                    </span>
                  </td>
                  <td className="px-2.5 py-1.5 text-carbon-gray-50">
                    <span className="font-plex-mono text-[10px]">Order: {o.orderNo}</span>
                    {o.pickNumber && <span className="font-plex-mono text-[10px] ml-2 text-carbon-gray-40">Pick: {o.pickNumber}</span>}
                  </td>
                  <td className="px-2.5 py-1.5 font-plex-mono text-[10px] text-carbon-gray-50">PO: {o.poNo || '-'}</td>
                  <td className="px-2.5 py-1.5 font-plex-mono text-[10px] text-carbon-gray-40">{o.shipWindow || '-'}</td>
                  <td className="px-2.5 py-1.5 text-right font-plex-mono text-[10px] text-carbon-gray-40">{o.startDate ? o.startDate.slice(0, 10) : '-'}</td>
                  <td className="px-2.5 py-1.5 text-right font-plex-mono text-carbon-gray-50">{fmt(o.orderQty)}</td>
                  <td className="px-2.5 py-1.5 text-right font-plex-mono text-carbon-gray-50">{fmtCur(o.grossPrice)}</td>
                  <td className="px-2.5 py-1.5 text-right font-plex-mono text-carbon-gray-40">{fmtCur(o.cost)}</td>
                  <td className="px-2.5 py-1.5 text-right font-plex-mono text-carbon-gray-40">{fmtCur(o.grossProfit)}</td>
                  <td className="px-2.5 py-1.5 text-right font-plex-mono font-medium text-carbon-gray-50">{fmtPct(o.gpPercent)}</td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

// One anomaly (concept row: 3px severity rail + icon tile + title/detail +
// count column) followed by its Pick / weekly Open sub-sections. Replaces the
// pre-reskin MissingCostSection + ValidatePriceSection pair — same grouping,
// same totals, one component.
function AlertSection({ rows, sev, icon: Icon, title, ruleNote }) {
  const { pickRows, openWeeks } = splitBySection(rows);
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const S = SEV[sev];

  const toggleSection = (key) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // "styles" counts unique (style, customer) pairs — a product-attribute
  // metric (distinct merchandise styles), NOT the count of expandable
  // groups. Group rows are keyed by (style, customer, division, company)
  // via getGroupKey above; same merchandise sold to same customer across
  // multiple divisions is intentionally ONE style here.
  const sectionTotal = (sRows) => ({
    count: sRows.length,
    value: sRows.reduce((s, r) => s + r.grossPrice, 0),
    styles: new Set(sRows.map(r => `${r.style}||${r.customer}`)).size,
  });

  const pickTotals = sectionTotal(pickRows);
  const totalValue = rows.reduce((s, r) => s + r.grossPrice, 0);

  const subHeader = ({ key, icon: SubIcon, label, note, totals, alarm }) => (
    <div
      className="flex items-center justify-between gap-3 px-[18px] py-2 bg-carbon-gray-10 border-t border-carbon-border cursor-pointer select-none"
      onClick={() => toggleSection(key)}
    >
      <div className="flex items-center gap-2 min-w-0">
        {collapsedSections.has(key)
          ? <ChevronRight size={13} className="text-carbon-gray-50 flex-shrink-0" />
          : <ChevronDown size={13} className="text-carbon-gray-50 flex-shrink-0" />}
        <SubIcon size={13} className="flex-shrink-0" style={{ color: alarm ? '#DA1E28' : '#0F62FE' }} />
        <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold" style={{ color: alarm ? '#DA1E28' : '#161616' }}>
          {label}
        </span>
        {note && <span className="text-[11px] text-carbon-gray-50 truncate">{note}</span>}
      </div>
      <div className="flex items-center gap-2 font-plex-mono text-[10px] text-carbon-gray-70 flex-shrink-0">
        <span>{totals.styles} STYLES</span>
        <span className="text-carbon-gray-40">·</span>
        <span>{totals.count} ORDERS</span>
        <span className="text-carbon-gray-40">·</span>
        <span>{fmtCur(totals.value)}</span>
      </div>
    </div>
  );

  return (
    <div>
      {/* Anomaly row — severity rail + icon tile + count column */}
      <div className="relative flex items-start gap-3.5 px-[18px] py-3 border-t border-carbon-gray-10">
        <span className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: rows.length > 0 ? S.rail : 'transparent' }} />
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0" style={{ background: S.tileBg, color: S.ink }}>
          <Icon size={13} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold">{title}</div>
          <div className="text-[12.5px] text-carbon-gray-70 mt-0.5">
            {ruleNote} · {rows.length > 0 ? fmtCur(totalValue) : 'no open alerts'}
          </div>
        </div>
        <span className="font-plex-mono text-xs font-semibold flex-shrink-0" style={{ color: rows.length > 0 ? S.ink : '#8D8D8D' }}>
          {rows.length}
        </span>
      </div>

      {/* PICK section — can ship any moment */}
      {pickRows.length > 0 && (
        <div>
          {subHeader({ key: 'pick', icon: Zap, label: 'Pick orders', note: 'can ship any moment', totals: pickTotals, alarm: false })}
          {!collapsedSections.has('pick') && (
            <div className="overflow-x-auto">
              <StyleGroupTable rows={pickRows} />
            </div>
          )}
        </div>
      )}

      {/* OPEN sections by week */}
      {openWeeks.map(week => {
        const totals = sectionTotal(week.rows);
        const sKey = `open-${week.monday}`;
        return (
          <div key={sKey}>
            {subHeader({ key: sKey, icon: Calendar, label: week.label, note: null, totals, alarm: week.isPastDue })}
            {!collapsedSections.has(sKey) && (
              <div className="overflow-x-auto">
                <StyleGroupTable rows={week.rows} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function AlertsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const result = await httpsCallable(functions, 'getMarginAlerts')();
      setData(result.data);
    } catch (err) {
      console.error('Failed to load margin alerts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const summary = data?.summary;
  const totalOpen = summary ? (summary.missingCostCount || 0) + (summary.validatePriceCount || 0) : 0;
  const atRiskValue = summary ? (summary.missingCostValue || 0) + (summary.validatePriceValue || 0) : 0;

  // KPI strip mapped to the REAL data this page computes. The concept's
  // "RESOLVED — 7D" tile is omitted (no resolved-alert data exists);
  // AT-RISK VALUE (real) takes its slot.
  const kpis = summary ? [
    { label: 'OPEN ALERTS', value: fmt(totalOpen), note: 'orders flagged', color: totalOpen > 0 ? '#DA1E28' : '#161616' },
    { label: 'MISSING COST', value: fmt(summary.missingCostCount), note: fmtCur(summary.missingCostValue), color: '#161616' },
    { label: 'VALIDATE PRICE', value: fmt(summary.validatePriceCount), note: fmtCur(summary.validatePriceValue), color: '#161616' },
    { label: 'AT-RISK VALUE', value: fmtCur(atRiskValue), note: 'gross price, open + pick', color: '#161616' },
  ] : [];

  return (
    <div className="font-plex text-carbon-ink space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold tracking-[-0.01em]">Alerts</h1>
          <p className="text-[12.5px] text-carbon-gray-70 mt-0.5">Sales order margin alerts — confirmed open &amp; pick orders</p>
        </div>
        <button onClick={fetchData} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-carbon-white border border-carbon-gray-30 text-[12.5px] font-medium text-carbon-ink hover:bg-carbon-gray-10 disabled:opacity-50">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <ErrorBanner what="Couldn't load margin alerts." staleNote={error} onRetry={fetchData} />
      )}

      {/* KPI strip — Carbon tile grid, 1px gaps on the border */}
      {summary && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-px bg-carbon-border border border-carbon-border">
          {kpis.map((k) => (
            <div key={k.label} className="bg-carbon-white px-4 py-3.5">
              <div className="font-plex-mono text-[9.5px] tracking-[0.12em] uppercase text-carbon-gray-50">{k.label}</div>
              <div className="flex items-baseline gap-1.5 mt-1.5">
                <span className="font-plex-mono text-xl font-semibold" style={{ color: k.color }}>{k.value}</span>
                <span className="text-[11px] text-carbon-gray-50">{k.note}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading — shimmer skeleton (canonical state) */}
      {loading && (
        <div className="bg-carbon-white border border-carbon-border">
          <SkeletonRows rows={5} />
        </div>
      )}

      {/* Anomaly list */}
      {data && !loading && (
        <div className="bg-carbon-white border border-carbon-border">
          <div className="flex items-center justify-between px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10">
            <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Margin anomalies</span>
            <span className="font-plex-mono text-[10px] tracking-[0.08em] uppercase text-carbon-gray-50">GP% rules · open + pick orders</span>
          </div>
          {totalOpen === 0 ? (
            <EmptyGoodNews detail="No margin anomalies on confirmed open & pick orders." />
          ) : (
            <>
              <AlertSection
                rows={data.missingCost || []}
                sev="high"
                icon={DollarSign}
                title="Potential missing cost"
                ruleNote="GP% above 85% — cost sheet likely missing. Pick first, then Open by start date"
              />
              <AlertSection
                rows={data.validatePrice || []}
                sev="med"
                icon={Tag}
                title="Validate price"
                ruleNote="GP% below 10% — selling price may need review"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
